<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useTemplateRefsList } from '@vueuse/core';

import api from '@/api';
import { RelationshipType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';

import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
// import SimpleComprehensive from './modules/SimpleComprehensive.vue';
import SimpleExpensesForm from './modules/Expenses/SimpleExpensesForm.vue';

import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import type { ApplicantId } from '@/types';

const { loanApp, loanApplicants, sectionStates, saveAndGoNext, goBack } = useCustomerQuoteProcess();

const isReady = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);

const expenseSectionType = computed(() => sectionStates.value.expenses);

const applicantsMarried = computed(() =>
  loanApp.value.applicants.some((applicant) => applicant.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO),
);

const validApplicants = computed(() => {
  if (applicantsMarried.value) {
    return [loanApp.value.applicants[0].id];
  }
  return loanApp.value.applicants.map((a) => a.id);
});

const financials = ref(cloneDeep(loanApp.value.financials));
const comprehensiveExpensesFormsRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();
const simpleExpensesFormsRefs = useTemplateRefsList<InstanceType<typeof SimpleExpensesForm>>();
const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 ? validApplicants.value[0] : undefined,
);

const isCompleted = (id: ApplicantId) => financials.value[id]?.expenses?.numberOfDependants !== undefined;

const validateExpenses = async () => {
  let isValid = true;

  if (expenseSectionType.value === 'simple') {
    const refCount = simpleExpensesFormsRefs.value.length;
    if (refCount > 0) {
      for (let i = 0; i < refCount; i++) {
        const formIsValid = (await simpleExpensesFormsRefs.value[i].update()) ?? false;
        if (!formIsValid) {
          isValid = false;
        }
      }
      if (!isValid) {
        window.scrollTo(0, 0);
      }
    } else {
      isValid = false;
    }
  } else {
    const refCount = comprehensiveExpensesFormsRefs.value.length;
    for (let i = 0; i < refCount; i++) {
      const formInstance = comprehensiveExpensesFormsRefs.value[i];
      if (formInstance) {
        isValid = (await formInstance.update()) ?? false;
        if (!isValid) {
          activeTab.value = validApplicants.value[i];
          window.scrollTo(0, 0);
          break;
        }
      } else {
        isValid = false;
        break;
      }
    }
  }
  return isValid;
};

const saveAndContinue = async () => {
  if (await validateExpenses()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};

const getSimpleExpenseModel = (applicantId: ApplicantId) => {
  return applicantsMarried.value ? financials.value[loanApp.value.applicants[0].id] : financials.value[applicantId];
};

onBeforeMount(async () => {
  isReady.value = false;
  try {
    const expenseTypesResponse = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    if (expenseTypesResponse.expenseTypes?.length) {
      expenseTypesResponse.expenseTypes.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      expenseTypes.value = expenseTypesResponse.expenseTypes;
    }
  } finally {
    isReady.value = true;
  }
});

watch(
  expenseSectionType,
  (newType, oldType) => {
    const isRelevantChange =
      (newType === 'simple' && oldType === 'comprehensive') || (newType === 'comprehensive' && oldType === 'simple');

    if (!isRelevantChange) return;

    for (const applicantId of validApplicants.value) {
      if (financials.value[applicantId]) {
        financials.value[applicantId].expenses = null;
      }
    }
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="isReady" class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Enter
        <BxHighlight>expenses</BxHighlight>
        for each household
      </h2>
    </div>
    <!-- <div class="my-4">
      <SimpleComprehensive :section-key="'expenses'" title="Expenses" />
      <p class="pt-4">
        Use the Quick vs Comprehensive toggle to choose whether you want to enter a total monthly living expense amount
        or a full breakdown of expense categories.
      </p>
    </div> -->
    <div v-if="applicantsMarried" class="my-6">
      <p>
        Please include your combined household expenses including
        {{ loanApp.applicants[1].preferredName || loanApp.applicants[1].firstName }}.
      </p>
    </div>
    <div class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <div v-if="expenseSectionType === 'simple'">
          <BxCard>
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customerExpense" /></span>
              <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
            </h3>
            <SimpleExpensesForm
              :ref="simpleExpensesFormsRefs.set"
              v-model="getSimpleExpenseModel(applicantId).expenses"
              :applicant-id="applicantId"
              :expense-types="expenseTypes"
            />
          </BxCard>
        </div>
        <div v-else>
          <BxCard :class="applicantId !== activeTab && 'hidden'">
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customerExpense" /></span>
              <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
            </h3>
            <ExpensesForm
              :ref="comprehensiveExpensesFormsRefs.set"
              v-model="financials[applicantId].expenses"
              :applicant-id="applicantId"
              :animate="applicantId === activeTab"
              :expense-types="expenseTypes"
              :show-buttons="validApplicants.length > 1"
              @save="activeTab = undefined"
              @cancel="activeTab = undefined"
            />
          </BxCard>
          <BxCard :class="applicantId === activeTab && 'hidden'">
            <div class="flex items-center">
              <div class="card-details">
                <div class="font-medium">
                  <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
                  <span class="ml-6 font-normal text-gray-400"
                    >({{ isCompleted(applicantId) ? 'completed' : 'incomplete' }})</span
                  >
                </div>
              </div>
              <div class="card-button">
                <BxButton
                  variant="secondary"
                  :disabled="activeTab && activeTab !== applicantId"
                  @click="activeTab = applicantId"
                >
                  {{ isCompleted(applicantId) ? 'Edit' : 'Add' }} expenses
                </BxButton>
              </div>
            </div>
          </BxCard>
        </div>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.section-panel {
  --border-radius: 0.5rem;
  --border-color: #ccc;

  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  background-color: #fff;
}

.card-details {
  flex-grow: 1;
}

.card-button {
  flex-basis: 200px;

  text-align: right;
}
</style>
